/**
 * Global points reward animation system.
 * Fires flying coin particles from a source position to the navbar points badge.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Coins } from "lucide-react";
import { sfxReward } from "@/lib/sfx";
import { supabase } from "@/integrations/supabase/client";
import { triggerRankUp } from "@/components/RankUpCelebration";

// ─── Event bus ───────────────────────────────────────────────
type PointsEvent = {
  points: number;
  sourceX?: number;
  sourceY?: number;
};

type Listener = (e: PointsEvent) => void;
const listeners = new Set<Listener>();

/** Call from anywhere to trigger the flying-coins animation.
 *  Pass the click/event coords as source; defaults to center-screen. */
export const triggerPointsAnimation = (points: number, sourceX?: number, sourceY?: number) => {
  listeners.forEach((fn) => fn({ points, sourceX, sourceY }));
};

// ─── Particle type ───────────────────────────────────────────
interface Particle {
  id: number;
  x: number;
  y: number;
  targetX: number;
  targetY: number;
  delay: number;
  label: string;
}

let nextId = 0;

const NAVBAR_TARGET_ID = "republic-points-target";

/* ─── Rank tiers (must match RepublicPoints.tsx) ─── */
const RANK_TIERS = [
  { min: 0, label: "Opener", emoji: "🎤" },
  { min: 500, label: "Crowd Surfer", emoji: "🏄" },
  { min: 2000, label: "Headliner", emoji: "🎸" },
  { min: 5000, label: "Rockstar", emoji: "🌟" },
  { min: 10000, label: "Legend", emoji: "👑" },
];

const getRank = (points: number) => {
  for (let i = RANK_TIERS.length - 1; i >= 0; i--) {
    if (points >= RANK_TIERS[i].min) return RANK_TIERS[i];
  }
  return RANK_TIERS[0];
};

// ─── Component (mount once in App) ──────────────────────────
const PointsRewardAnimation = () => {
  const [particles, setParticles] = useState<Particle[]>([]);
  const [badge, setBadge] = useState<{ show: boolean; points: number }>({ show: false, points: 0 });
  const lastKnownPoints = useRef<number | null>(null);

  const handleEvent = useCallback((e: PointsEvent) => {
    // Find target position
    const target = document.getElementById(NAVBAR_TARGET_ID);
    const targetRect = target?.getBoundingClientRect();
    const tx = targetRect ? targetRect.left + targetRect.width / 2 : window.innerWidth - 120;
    const ty = targetRect ? targetRect.top + targetRect.height / 2 : 32;

    const sx = e.sourceX ?? window.innerWidth / 2;
    const sy = e.sourceY ?? window.innerHeight / 2;

    sfxReward();

    // Create 5-8 coin particles
    const count = Math.min(Math.max(3, e.points), 8);
    const newParticles: Particle[] = [];
    for (let i = 0; i < count; i++) {
      newParticles.push({
        id: ++nextId,
        x: sx + (Math.random() - 0.5) * 40,
        y: sy + (Math.random() - 0.5) * 40,
        targetX: tx,
        targetY: ty,
        delay: i * 0.07,
        label: i === 0 ? `+${e.points}` : "",
      });
    }
    setParticles((prev) => [...prev, ...newParticles]);

    // Show badge pulse on navbar after coins arrive
    const arrivalTime = (count * 0.07 + 0.55) * 1000;
    setTimeout(() => {
      setBadge({ show: true, points: e.points });
      // Pulse the navbar element
      const el = document.getElementById(NAVBAR_TARGET_ID);
      if (el) {
        el.classList.add("points-pulse");
        setTimeout(() => el.classList.remove("points-pulse"), 600);
      }
    }, arrivalTime);

    setTimeout(() => setBadge({ show: false, points: 0 }), arrivalTime + 1500);

    // Clean up particles after animation
    setTimeout(() => {
      const ids = new Set(newParticles.map((p) => p.id));
      setParticles((prev) => prev.filter((p) => !ids.has(p.id)));
    }, arrivalTime + 200);

    // Check for rank-up after points are awarded
    setTimeout(async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        const { data: profile } = await supabase
          .from("profiles")
          .select("republic_points")
          .eq("user_id", user.id)
          .single();
        if (!profile) return;

        const newPoints = profile.republic_points;
        const oldPoints = lastKnownPoints.current;
        lastKnownPoints.current = newPoints;

        if (oldPoints !== null && oldPoints !== newPoints) {
          const oldRank = getRank(oldPoints);
          const newRank = getRank(newPoints);
          if (newRank.min > oldRank.min) {
            // Delay slightly so coin animation finishes first
            setTimeout(() => triggerRankUp(newRank.label, newRank.emoji), 500);
          }
        }
      } catch {}
    }, arrivalTime + 400);
  }, []);

  useEffect(() => {
    listeners.add(handleEvent);
    return () => { listeners.delete(handleEvent); };
  }, [handleEvent]);

  // Initialize lastKnownPoints on mount
  useEffect(() => {
    const init = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        const { data: profile } = await supabase
          .from("profiles")
          .select("republic_points")
          .eq("user_id", user.id)
          .single();
        if (profile) lastKnownPoints.current = profile.republic_points;
      } catch {}
    };
    init();
  }, []);

  return (
    <div className="fixed inset-0 z-[100] pointer-events-none" aria-hidden>
      <AnimatePresence>
        {particles.map((p) => (
          <motion.div
            key={p.id}
            initial={{
              x: p.x,
              y: p.y,
              scale: 0,
              opacity: 0,
            }}
            animate={{
              x: [p.x, p.x + (Math.random() - 0.5) * 80, p.targetX],
              y: [p.y, p.y - 40 - Math.random() * 60, p.targetY],
              scale: [0, 1.2, 0.6],
              opacity: [0, 1, 0.8, 0],
            }}
            transition={{
              duration: 0.55,
              delay: p.delay,
              ease: [0.22, 1, 0.36, 1],
              times: [0, 0.3, 0.8, 1],
            }}
            className="absolute"
            style={{ left: 0, top: 0 }}
          >
            <div className="flex items-center gap-1">
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary shadow-lg shadow-primary/40">
                <Coins className="h-3.5 w-3.5 text-primary-foreground" />
              </div>
              {p.label && (
                <span className="text-xs font-display font-bold text-primary drop-shadow-md">
                  {p.label}
                </span>
              )}
            </div>
          </motion.div>
        ))}
      </AnimatePresence>

      {/* Badge that appears at the navbar target after coins arrive */}
      <AnimatePresence>
        {badge.show && (
          <motion.div
            initial={{ scale: 0, opacity: 0, y: -10 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0, opacity: 0, y: -10 }}
            transition={{ type: "spring", stiffness: 500, damping: 25 }}
            className="absolute"
            style={{
              left: (document.getElementById(NAVBAR_TARGET_ID)?.getBoundingClientRect().left ?? window.innerWidth - 120) - 4,
              top: (document.getElementById(NAVBAR_TARGET_ID)?.getBoundingClientRect().bottom ?? 48) + 4,
            }}
          >
            <div className="rounded-full bg-primary px-2.5 py-0.5 shadow-lg shadow-primary/30">
              <span className="text-xs font-display font-bold text-primary-foreground">
                +{badge.points} pts
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PointsRewardAnimation;
export { NAVBAR_TARGET_ID };
